import { baseApi } from "./baseApi";

export type Role = { id: string; name: string };
export type Branch = { id: string; branch_detail?: { branch_name: string } };
export type Company = { id: string; company_name: string };

export const inviteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // create invitation
    createInvitation: builder.mutation<
      any,
      {
        first_name: string;
        last_name: string;
        email: string;
        phone_number: string;
        gender: string;
        resumption_date: string;
        branch_id: string;
        company_id: string;
        role_id: string;
      }
    >({
      query: (body) => ({
        url: "/invites/create-invitation",
        method: "POST",
        body,
      }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // get all invitations
    getAllInvitations: builder.query<any, void>({
      query: () => ({ url: "/invites/all-invite", method: "GET" }),
      providesTags: ["invites"],
    }),

    // update invitation
    updateInvitation: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/invites/update/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    //delete invitation
    deleteInvitation: builder.mutation<any, string>({
      query: (id) => ({ url: `/invites/delete/${id}`, method: "DELETE" }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // resend invitation
    resendInvitation: builder.mutation<any, string>({
      query: (id) => ({ url: `/invites/invites/${id}/resend`, method: "POST" }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // accept invitation
    acceptInvitation: builder.mutation<any, { id: string; password: string; password_confirmation: string }>({
      query: ({ id, password, password_confirmation }) => ({
        url: `/invite/${id}/accept`,
        method: "POST",
        body: { password, password_confirmation },
      }),
      invalidatesTags: ["invites"],
    }),

    // reject invitation
    rejectInvitation: builder.mutation<any, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/invite/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["invites"],
    }),


    // bulk upload
    bulkUploadStaffInvitations: builder.mutation<any, FormData>({
      query: (formData) => ({ url: "/invites/bulk-upload", method: "POST", body: formData }),
      invalidatesTags: ["planLimit", "invites"],
    }),

    // download template
    downloadStaffInvitationTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "/invites/download-template",
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    // invite stats
    getInviteStats: builder.query<any, void>({
      query: () => ({ url: "/staffs/statistics", method: "GET" }),
      providesTags: ["invite-stats"],
    }),

    // fetch roles
    getRoles: builder.query<Role[], void>({
      query: () => ({ url: "/role", method: "GET" }),
      transformResponse: (response: any) => response?.data?.roleInfo?.data || [],
      providesTags: ["roles"],
    }),

    // fetch branches
    getBranches: builder.query<Branch[], void>({
      query: () => ({ url: "/branches/all-branches", method: "GET" }),
      transformResponse: (response: any) => response?.data?.branchInfo?.data || [],
      providesTags: ["branches"],
    }),

    // fetch companies
    getCompanies: builder.query<Company | null, void>({
      query: () => ({ url: "/company/get-company", method: "GET" }),
      transformResponse: (response: any) => response?.data?.companyInfo || null,
      providesTags: ["companies"],
    }),
    getSingleInvitedUser: builder.query<any, string | number>({
      query: (id) => ({
        url: `/invite/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "users", id }],
      transformResponse: (response: any) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateInvitationMutation: useCreateInviteMutation,
  useGetAllInvitationsQuery,
  useUpdateInvitationMutation,
  useDeleteInvitationMutation,
  useResendInvitationMutation,
  useAcceptInvitationMutation,
  useRejectInvitationMutation,
  useBulkUploadStaffInvitationsMutation,
  useGetInviteStatsQuery,
  useGetRolesQuery: useGetAllRolesQuery,
  useGetBranchesQuery: useGetAllBranchesQuery,
  useGetCompaniesQuery,
  useLazyDownloadStaffInvitationTemplateQuery,
  useGetSingleInvitedUserQuery
} = inviteApi;
